// src/whatsappChat/api/content.js
// Helpers that turn API rows into something safe to render. Inbound non-text
// content is not documented by BotSense, so everything here parses defensively.

const MEDIA_TYPES = ['image', 'video', 'audio', 'document', 'sticker'];
const IST = 'Asia/Kolkata';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function parseJson(value) {
  if (value == null) return null;
  if (typeof value === 'object') return value;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

const str = (value) => (typeof value === 'string' && value.trim() ? value : null);

const firstStr = (...values) => {
  for (const value of values) {
    const found = str(value);
    if (found) return found;
  }
  return null;
};

const httpLink = (value) => (str(value) && /^https?:\/\//i.test(value) ? value : null);

const TEXT_KEYS = ['title', 'text', 'body', 'caption', 'message', 'content', 'value'];

function readableFrom(value, depth = 0) {
  if (depth > 6 || value == null) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    // A string that is itself JSON (content is sometimes encoded twice)
    if (/^[[{]/.test(trimmed)) {
      const parsed = parseJson(trimmed);
      if (parsed) return readableFrom(parsed, depth + 1);
    }
    return value;
  }
  if (Array.isArray(value)) {
    const parts = value.map((item) => readableFrom(item, depth + 1)).filter(Boolean);
    return parts.length ? parts.join('\n') : null;
  }
  if (typeof value !== 'object') return null;

  const reply = value.interactive || value;
  const nested = [reply.button_reply, reply.list_reply, value.button, value.interactive];
  for (const item of nested) {
    const found = readableFrom(item, depth + 1);
    if (found) return found;
  }
  for (const key of TEXT_KEYS) {
    const found = readableFrom(value[key], depth + 1);
    if (found) return found;
  }
  return null;
}

/**
 * The human-readable text of a message body or inbox preview. Inbound content can
 * arrive as a JSON array/object (or a JSON string of one) instead of plain text.
 */
export function readableText(value) {
  if (value == null) return '';
  const found = readableFrom(value);
  if (found) return found;
  return typeof value === 'string' ? value : '';
}

export function fillTemplate(body, variables = []) {
  if (!str(body)) return '';
  return body.replace(/\{\{\s*(\d+)\s*\}\}/g, (match, n) => {
    const value = variables[Number(n) - 1];
    return value == null || value === '' ? match : String(value);
  });
}

/**
 * View model for one message:
 * { kind, label, text, header, headerMedia, footer, link, filename, mediaType, buttons, sections, cta, templateName, resolved }
 */
export function describeMessage(msg) {
  const type = msg?.type || 'unknown';
  const outbound = msg?.direction === 'outbound';

  if (type === 'text') {
    return { kind: 'text', text: readableText(msg.content) };
  }

  if (type === 'template') {
    // `template` is the message as the customer received it; null when it could not be resolved.
    const tpl = msg.template && typeof msg.template === 'object' ? msg.template : null;
    const headerType = String(tpl?.header_type || '').toUpperCase();
    return {
      kind: 'template',
      templateName: firstStr(tpl?.name, msg.template_name) || 'template',
      resolved: Boolean(tpl),
      header: str(tpl?.header),
      headerMedia: ['IMAGE', 'VIDEO', 'DOCUMENT', 'LOCATION'].includes(headerType) ? `[${headerType.toLowerCase()}]` : null,
      text: str(tpl?.body),
      footer: str(tpl?.footer),
      buttons: Array.isArray(tpl?.buttons) ? tpl.buttons.map((b) => str(b?.text)).filter(Boolean) : [],
    };
  }

  const parsed = parseJson(msg?.content);
  // Inbound payloads are sometimes wrapped in a one-item array
  const c = Array.isArray(parsed) ? parsed.find((item) => item && typeof item === 'object') || null : parsed;

  if (MEDIA_TYPES.includes(type)) {
    const inner = c?.[type] && typeof c[type] === 'object' ? c[type] : null;
    return {
      kind: 'media',
      mediaType: type,
      label: `[${type}]`,
      link: httpLink(c?.link) || httpLink(c?.url) || httpLink(c?.media_url) || httpLink(inner?.link) || httpLink(inner?.url),
      text: firstStr(c?.caption, inner?.caption),
      filename: firstStr(c?.filename, inner?.filename),
    };
  }

  if (type === 'interactive' && outbound && c) {
    const sections = Array.isArray(c.sections) ? c.sections : [];
    return {
      kind: 'interactive',
      header: firstStr(c.header, c.header_text),
      text: firstStr(c.text),
      footer: firstStr(c.footer, c.footer_text),
      buttons: Array.isArray(c.buttons) ? c.buttons.map((b) => str(b?.title)).filter(Boolean) : [],
      listButton: str(c.list_button),
      sections: sections.map((s) => ({
        title: str(s?.title),
        rows: Array.isArray(s?.rows) ? s.rows.filter((r) => str(r?.title)) : [],
      })),
      cta: str(c.cta_text) ? { text: c.cta_text, url: httpLink(c.cta_url) } : null,
    };
  }

  // Anything else, including the customer's tap on a button or list row.
  const reply = parsed ? readableFrom(parsed) : null;
  if (reply) return { kind: 'text', text: reply, label: `[${type}]` };

  // Plain string that was not JSON
  if (!parsed && str(msg?.content)) return { kind: 'text', text: msg.content, label: `[${type}]` };

  return { kind: 'unknown', label: `[${type}]` };
}

export function describeError(msg) {
  if (msg?.status !== 'failed') return null;
  const e = parseJson(msg.error);
  // Unparseable error: show the raw string. No reason at all: null.
  return firstStr(e?.title, e?.message, e ? null : msg.error);
}

// ---- Times: the API sends `YYYY-MM-DD HH:mm:ss` already in IST; never re-zone it. ----

function splitStamp(stamp) {
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(stamp || '');
  if (!m) return null;
  return { date: `${m[1]}-${m[2]}-${m[3]}`, y: +m[1], mo: +m[2], d: +m[3], h: +m[4], mi: m[5] };
}

const istDate = (offsetDays = 0) =>
  new Date(Date.now() + offsetDays * 86400000).toLocaleDateString('en-CA', { timeZone: IST });

export function formatTime(stamp) {
  const t = splitStamp(stamp);
  if (!t) return '';
  return `${t.h % 12 || 12}:${t.mi} ${t.h < 12 ? 'AM' : 'PM'}`;
}

export function formatDay(stamp) {
  const t = splitStamp(stamp);
  if (!t) return '';
  if (t.date === istDate()) return 'Today';
  if (t.date === istDate(-1)) return 'Yesterday';
  return `${String(t.d).padStart(2, '0')} ${MONTHS[t.mo - 1]} ${t.y}`;
}

/** Inbox row: time for today, otherwise the day. */
export function formatListTime(stamp) {
  const t = splitStamp(stamp);
  if (!t) return '';
  if (t.date === istDate()) return formatTime(stamp);
  if (t.date === istDate(-1)) return 'Yesterday';
  return `${String(t.d).padStart(2, '0')} ${MONTHS[t.mo - 1]}`;
}

export const dayKey = (stamp) => splitStamp(stamp)?.date || '';

export function formatPhone(phone) {
  const digits = String(phone || '').replace(/\D/g, '');
  if (digits.length === 12 && digits.startsWith('91')) return `+91 ${digits.slice(2, 7)} ${digits.slice(7)}`;
  return digits ? `+${digits}` : '';
}

/** Dropdown text for a business number. The API's label is "Name (phone)", or the id when phone is null. */
export const numberLabel = (n) => n?.label || n?.name || n?.number_id || '';

// ---- Composing ----

export const MAX_TEXT = 4096;
export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

export function mediaTypeForFile(file) {
  const mime = String(file?.type || '');
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  return 'document';
}

export function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Highest {{n}} used in a string. */
export function variableCount(text) {
  let max = 0;
  String(text || '').replace(/\{\{\s*(\d+)\s*\}\}/g, (match, n) => {
    max = Math.max(max, Number(n));
    return match;
  });
  return max;
}

/**
 * A template's header: { type: 'TEXT'|'IMAGE'|'VIDEO'|'DOCUMENT'|'LOCATION'|'', text, mediaUrl }.
 * The header can arrive flat, under `components`, or as a Meta-style components list.
 */
export function templateHeader(tpl) {
  const comps = tpl?.components;
  const part = Array.isArray(comps) ? comps.find((c) => String(c?.type || '').toUpperCase() === 'HEADER') : null;
  const flat = !Array.isArray(comps) && comps && typeof comps === 'object' ? comps : null;
  const headerObj = tpl?.header && typeof tpl.header === 'object' ? tpl.header : null;

  const mediaUrl =
    httpLink(tpl?.header_media_url) ||
    httpLink(tpl?.header_url) ||
    httpLink(tpl?.media_url) ||
    httpLink(flat?.header_media_url) ||
    httpLink(flat?.header_url) ||
    httpLink(headerObj?.link) ||
    httpLink(headerObj?.url) ||
    httpLink(part?.example?.header_handle?.[0]) ||
    httpLink(tpl?.header) ||
    null;

  const text = [tpl?.header, headerObj?.text, flat?.header, part?.text].find((v) => str(v) && !httpLink(v)) || '';

  let type = String(
    tpl?.header_type ||
      tpl?.header_format ||
      flat?.header_type ||
      flat?.header_format ||
      headerObj?.type ||
      headerObj?.format ||
      part?.format ||
      ''
  ).toUpperCase();
  if (!type) {
    const hinted = String(tpl?.template_type || '').toUpperCase();
    if (['IMAGE', 'VIDEO', 'DOCUMENT', 'LOCATION'].includes(hinted)) type = hinted;
    else if (mediaUrl || tpl?.header_media_row_id || flat?.header_media_row_id) type = 'IMAGE';
    else if (text) type = 'TEXT';
  }

  return { type, text, mediaUrl };
}

/**
 * What a template needs filled in before it can be sent:
 * { bodyCount, headerMedia: 'image'|'video'|'document'|null, buttons: [{ index, subType, label }], supported }
 */
export function templateRequirements(tpl) {
  const headerType = templateHeader(tpl).type;
  const buttons = [];
  (Array.isArray(tpl?.buttons) ? tpl.buttons : []).forEach((button, index) => {
    const type = String(button?.type || '').toUpperCase();
    if (type === 'URL' && variableCount(button?.url) > 0) {
      buttons.push({ index, subType: 'url', label: `Link value for "${button.text || 'button'}"` });
    } else if (type === 'COPY_CODE') {
      buttons.push({ index, subType: 'copy_code', label: 'Coupon code' });
    }
  });
  return {
    bodyCount: variableCount(tpl?.body),
    headerMedia: ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerType) ? headerType.toLowerCase() : null,
    buttons,
    supported: (tpl?.template_type || 'standard') === 'standard',
  };
}

export const isHttpsUrl = (value) => /^https:\/\/\S+$/i.test(String(value || '').trim());

export const digitsOnly = (value) => String(value || '').replace(/\D/g, '');

export function initials(name, phone) {
  const words = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (words.length) return (words[0][0] + (words[1]?.[0] || '')).toUpperCase();
  return String(phone || '').slice(-2) || '?';
}
